import type { AnchorEntry } from "./useTaskCreation";

export type ParseResult =
  | { error: null; anchors: AnchorEntry[] }
  | { error: string; anchors: never[] };

export type RelevantTopicParsedRow = {
  text: string;
  articleCount: number;
  uniqueness: number | null;
  generalTopic: string | null;
};

export type RelevantTopicParseResult =
  | { error: null; rows: RelevantTopicParsedRow[] }
  | { error: string; rows: never[] };

/**
 * Разбор текста анкоров из формы создания задачи.
 *
 * Строка читается справа налево: поля отделяются последней запятой или точкой с
 * запятой, поэтому запятые внутри самого анкора не ломают разбор. Простая форма —
 * `anchor,количество`; расширенная (релевантные темы) — `anchor,количество,
 * уникальность,тема`.
 */
export class AnchorsText {
  public static parse(text: string): ParseResult {
    let parseOutcome: ParseResult;
    const lines = text
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line !== "");
    const anchors: AnchorEntry[] = [];
    const invalidLines: string[] = [];
    for (const line of lines) {
      const delimiterIndex = AnchorsText.findLastDelimiter(line);
      if (delimiterIndex === -1) {
        invalidLines.push(line);
        continue;
      }
      const anchorText = line.slice(0, delimiterIndex).trim();
      const countStr = line.slice(delimiterIndex + 1).trim();
      const count = Number(countStr);
      const countIsPositive = !Number.isNaN(count) && count >= 1;
      if (anchorText === "" || !countIsPositive) {
        invalidLines.push(line);
        continue;
      }
      anchors.push({ text: anchorText, articleCount: count });
    }
    if (invalidLines.length > 0) {
      parseOutcome = {
        error: `Некорректный формат строк: ${invalidLines.join(", ")}. Ожидается: anchor,количество`,
        anchors: [],
      };
    } else {
      parseOutcome = { error: null, anchors };
    }
    return parseOutcome;
  }



  public static parseRelevantTopic(text: string): RelevantTopicParseResult {
    let relevantTopic: RelevantTopicParseResult;
    const lines = text
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line !== "");
    const rows: RelevantTopicParsedRow[] = [];
    const invalidLines: string[] = [];
    for (const line of lines) {
      const parsed = AnchorsText.parseRelevantTopicLine(line);
      if (!parsed) {
        invalidLines.push(line);
        continue;
      }
      rows.push(parsed);
    }
    if (invalidLines.length > 0) {
      relevantTopic = {
        error: `Некорректный формат строк: ${invalidLines.join(", ")}. Ожидается: anchor,количество или anchor,количество,уникальность,тема`,
        rows: [],
      };
    } else {
      relevantTopic = { error: null, rows };
    }
    return relevantTopic;
  }

  private static parseRelevantTopicLine(line: string): RelevantTopicParsedRow | null {
    let parsedRow: RelevantTopicParsedRow | null = null;
    const lastDelimiter = AnchorsText.findLastDelimiter(line);
    if (lastDelimiter !== -1) {
      const fullFormatRow = AnchorsText.parseFullFormatLine(line, lastDelimiter);
      if (fullFormatRow === null) {
        parsedRow = AnchorsText.parseShortFormatLine(line, lastDelimiter);
      } else {
        parsedRow = fullFormatRow;
      }
    }
    return parsedRow;
  }

  /** Расширенная форма строки: `anchor,количество,уникальность,тема`. */
  private static parseFullFormatLine(line: string, lastDelimiter: number): RelevantTopicParsedRow | null {
    let fullFormatRow: RelevantTopicParsedRow | null = null;
    const generalTopic = line.slice(lastDelimiter + 1).trim();
    const beforeTopic = line.slice(0, lastDelimiter);
    const uniquenessDelimiter = AnchorsText.findLastDelimiter(beforeTopic);
    const beforeUniqueness = uniquenessDelimiter === -1 ? "" : beforeTopic.slice(0, uniquenessDelimiter);
    const uniqueness =
      uniquenessDelimiter === -1 ? Number.NaN : Number(beforeTopic.slice(uniquenessDelimiter + 1).trim());
    const countDelimiter = AnchorsText.findLastDelimiter(beforeUniqueness);
    const anchorText = countDelimiter === -1 ? "" : beforeUniqueness.slice(0, countDelimiter).trim();
    const articleCount =
      countDelimiter === -1 ? Number.NaN : Number(beforeUniqueness.slice(countDelimiter + 1).trim());
    const uniquenessIsInRange = !Number.isNaN(uniqueness) && uniqueness > 0 && uniqueness <= 100;
    const countIsPositive = !Number.isNaN(articleCount) && articleCount >= 1;
    const isFullFormat = uniquenessIsInRange && countIsPositive && anchorText !== "" && generalTopic !== "";
    if (isFullFormat) {
      fullFormatRow = { text: anchorText, articleCount, uniqueness, generalTopic };
    }
    return fullFormatRow;
  }

  /** Простая форма строки: `anchor,количество`. */
  private static parseShortFormatLine(line: string, lastDelimiter: number): RelevantTopicParsedRow | null {
    let shortFormatRow: RelevantTopicParsedRow | null = null;
    const articleCount = Number(line.slice(lastDelimiter + 1).trim());
    const anchorText = line.slice(0, lastDelimiter).trim();
    const countIsPositive = !Number.isNaN(articleCount) && articleCount >= 1;
    const isShortFormat = countIsPositive && anchorText !== "";
    if (isShortFormat) {
      shortFormatRow = { text: anchorText, articleCount, uniqueness: null, generalTopic: null };
    }
    return shortFormatRow;
  }

  private static findLastDelimiter(line: string): number {
    const lastComma = line.lastIndexOf(",");
    const lastSemicolon = line.lastIndexOf(";");
    return Math.max(lastComma, lastSemicolon);
  }

}
